import { Plane, Building2, Car, Calendar, Users, Clock, MapPin, Heart } from 'lucide-react';

export interface ServiceDetail {
  id: string;
  title: string;
  slug: string;
  short_description: string;
  long_description: string;
  icon: string;
  image_url: string;
  features: string[];
  pricing: {
    type: string;
    description: string;
  };
  benefits: string[];
}

export const SERVICES: ServiceDetail[] = [
  {
    id: '1',
    title: 'Airport Transfers',
    slug: 'airport-transfers',
    short_description: 'Reliable and comfortable airport transportation services with flight tracking',
    long_description: 'Experience stress-free airport transfers with our premium chauffeur service. We monitor your flight in real-time, ensuring your driver is always there when you land - even if your flight is delayed. Our professional chauffeurs will meet you at arrivals with a personalized name sign and assist with your luggage.',
    icon: 'Plane',
    image_url: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1200&q=80',
    features: [
      'Real-time flight tracking',
      'Meet & greet at arrivals',
      'Complimentary 60-minute wait time',
      'Luggage assistance',
      'Fixed prices - no surge pricing',
      'Child seats available on request',
    ],
    pricing: {
      type: 'Fixed Rate',
      description: 'Prices start from €50 for Brussels Airport transfers',
    },
    benefits: [
      'No hidden fees or surprises',
      'Free cancellation up to 24 hours',
      'Professional uniformed chauffeurs',
      'Premium vehicle fleet',
    ],
  },
  {
    id: '2',
    title: 'Corporate Travel',
    slug: 'corporate-travel',
    short_description: 'Professional transportation solutions for business needs and executive travel',
    long_description: 'Elevate your business travel with our corporate transportation services. We understand the importance of punctuality and professionalism in the business world. Our executive chauffeurs are trained to provide discrete, reliable service that allows you to focus on what matters most - your business.',
    icon: 'Building2',
    image_url: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?auto=format&fit=crop&w=1200&q=80',
    features: [
      'Priority scheduling',
      'Corporate account management',
      'Monthly invoicing available',
      'Executive vehicle fleet',
      'Confidential and discrete service',
      'Wi-Fi equipped vehicles',
    ],
    pricing: {
      type: 'Corporate Rates',
      description: 'Special rates for corporate clients with volume discounts',
    },
    benefits: [
      'Dedicated account manager',
      'Flexible billing options',
      '24/7 support hotline',
      'Detailed trip reporting',
    ],
  },
  {
    id: '3',
    title: 'Hourly Service',
    slug: 'hourly-service',
    short_description: 'Flexible chauffeur service available by the hour for your convenience',
    long_description: 'Our hourly chauffeur service offers maximum flexibility for your transportation needs. Whether you need a driver for shopping trips, multiple appointments, or exploring the city, our chauffeurs are at your disposal. The vehicle and driver stay with you throughout your booking.',
    icon: 'Clock',
    image_url: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&w=1200&q=80',
    features: [
      'Minimum 2-hour booking',
      'Chauffeur stays with you',
      'Multiple stops included',
      'Flexible itinerary changes',
      'Complimentary refreshments',
      'Real-time booking extension',
    ],
    pricing: {
      type: 'Hourly Rate',
      description: 'Starting from €60/hour with a 2-hour minimum',
    },
    benefits: [
      'No distance limits',
      'Wait time included',
      'Change plans on the go',
      'Perfect for busy schedules',
    ],
  },
  {
    id: '4',
    title: 'Special Events',
    slug: 'special-events',
    short_description: 'Luxury transportation for weddings, parties, and special occasions',
    long_description: 'Make your special day even more memorable with our luxury event transportation. From elegant weddings to glamorous galas, we provide the perfect backdrop for your celebration. Our decorated vehicles and professionally attired chauffeurs ensure you arrive in style.',
    icon: 'Calendar',
    image_url: 'https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1200&q=80',
    features: [
      'Wedding car decoration',
      'Red carpet service',
      'Professional chauffeur attire',
      'Champagne service available',
      'Multiple vehicle coordination',
      'Photography-ready arrivals',
    ],
    pricing: {
      type: 'Event Package',
      description: 'Custom packages starting from €300 for wedding transfers',
    },
    benefits: [
      'Personalized service planning',
      'Backup vehicle guarantee',
      'Experienced event drivers',
      'Coordination with venue',
    ],
  },
  {
    id: '5',
    title: 'Group Transportation',
    slug: 'group-transportation',
    short_description: 'Comfortable group travel solutions for any size party',
    long_description: 'Traveling with a group? Our spacious luxury vans and coordinated multi-vehicle services ensure everyone travels together in comfort. Perfect for corporate teams, family gatherings, or group tours, we handle all the logistics so you can enjoy the journey.',
    icon: 'Users',
    image_url: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?auto=format&fit=crop&w=1200&q=80',
    features: [
      'Luxury Mercedes V-Class vans',
      'Groups up to 7 passengers',
      'Multi-vehicle coordination',
      'Ample luggage space',
      'Group booking discounts',
      'Tour guide integration',
    ],
    pricing: {
      type: 'Group Rate',
      description: 'Special group rates with discounts for 4+ passengers',
    },
    benefits: [
      'Everyone travels together',
      'Cost-effective solution',
      'Flexible pickup points',
      'Professional coordination',
    ],
  },
  {
    id: '6',
    title: 'City Tours',
    slug: 'city-tours',
    short_description: 'Discover Belgium\'s finest destinations with our guided chauffeur tours',
    long_description: 'Explore the beauty of Belgium with our curated city tours. From the historic streets of Bruges to the vibrant capital Brussels, our knowledgeable chauffeurs will guide you through the country\'s most iconic destinations. Customize your itinerary or choose from our popular tour packages.',
    icon: 'MapPin',
    image_url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80',
    features: [
      'Customizable itineraries',
      'Local knowledge & recommendations',
      'Popular destination packages',
      'Photo stop opportunities',
      'Multilingual chauffeurs',
      'All-day tour options',
    ],
    pricing: {
      type: 'Tour Package',
      description: 'Half-day tours from €200, full-day from €350',
    },
    benefits: [
      'Skip the crowds',
      'Personalized experience',
      'Door-to-door convenience',
      'Flexible timing',
    ],
  },
  {
    id: '7',
    title: 'Hourly Charter',
    slug: 'hourly-charter',
    short_description: 'Flexible chauffeur service by the hour for shopping, tours, or multiple appointments',
    long_description: 'Our hourly charter service provides you with a dedicated chauffeur and vehicle for as long as you need. Ideal for business meetings, shopping excursions, or city exploration, you have complete control over your schedule while we handle the driving.',
    icon: 'Car',
    image_url: 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&w=1200&q=80',
    features: [
      'Flexible scheduling',
      'Multiple destinations',
      'Vehicle at your disposal',
      'Professional chauffeur',
      'Real-time modifications',
      'Extension on demand',
    ],
    pricing: {
      type: 'Hourly Rate',
      description: 'From €65/hour with minimum 3-hour booking',
    },
    benefits: [
      'Complete flexibility',
      'No distance restrictions',
      'Wait time included',
      'Door-to-door service',
    ],
  },
  {
    id: '8',
    title: 'VIP & Concierge',
    slug: 'vip-concierge',
    short_description: 'Exclusive VIP services with personal concierge and luxury amenities',
    long_description: 'Experience the pinnacle of luxury transportation with our VIP concierge service. From personal assistants to exclusive restaurant reservations, we go beyond transportation to curate your entire experience. Our VIP team anticipates your needs before you even have to ask.',
    icon: 'Heart',
    image_url: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=1200&q=80',
    features: [
      'Personal concierge service',
      'Priority booking & scheduling',
      'Exclusive vehicle selection',
      'Restaurant & event reservations',
      'Personal shopping assistance',
      'Luxury amenities',
    ],
    pricing: {
      type: 'Premium Package',
      description: 'Custom VIP packages tailored to your requirements',
    },
    benefits: [
      'White-glove treatment',
      'Anticipatory service',
      '24/7 dedicated support',
      'Exclusive experiences',
    ],
  },
];

export const icons = {
  Plane,
  Building2,
  Car,
  Calendar,
  Users,
  Clock,
  MapPin,
  Heart,
};

export const getServiceBySlug = (slug: string): ServiceDetail | undefined => {
  return SERVICES.find(service => service.slug === slug);
};
